import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowDownUp,
  ArrowUp,
  Download,
  DollarSign,
  Edit3,
  FileDown,
  FileSpreadsheet,
  FileText,
  FileX,
  Folder,
  Info,
  Loader2,
  MoreHorizontal,
  Plus,
  Wallet,
} from 'lucide-react';
import { ExpandableListRow } from '@/components/shared/ExpandableListRow';
import { KpiCard } from '@/components/shared/KpiCard';
import { PageHeader } from '@/components/shared/PageHeader';
import { RefreshingIndicator } from '@/components/shared/RefreshingIndicator';
import { ResponsiveDetailGrid } from '@/components/shared/ResponsiveDetailGrid';
import { SlideOverPanel } from '@/components/shared/SlideOverPanel';
import { StateCard } from '@/components/shared/StateCard';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import {
  exportCollectedPaymentsPdf,
  exportReceivablePaymentsPdf,
  exportProjectPaymentsCsv,
  exportProjectStatementPdf,
  fetchProjectBillingLedger,
  sendProjectPaymentFollowup,
  type BillingDateRange,
  type BillingExportOverview,
  type BillingPaymentStatus,
  type BillingPaymentStatusFilter,
  type BillingProjectItem,
} from '@/lib/billing-exports';
import {
  createProjectPayment,
  deleteProjectPayment,
  updateProjectPayment,
  type ProjectPaymentItem,
  type ProjectPaymentMethod,
} from '@/lib/projects-clients';
import { invalidateProjectData } from '@/lib/query-invalidation';
import { queryKeys, useBillingOverview } from '@/hooks/use-app-queries';
import { formatCurrency, type ApprovalDeliveryMethod } from '@/lib/tickets';
import { SendPaymentFollowUpDialog } from '@/components/projects/SendPaymentFollowUpDialog';
import { useAuth } from '@/auth/auth-context';

const EMPTY_ITEMS: BillingProjectItem[] = [];
const DEFAULT_KPIS = {
  receivable: 0,
  collected: 0,
  outstandingProjects: 0,
};

type PaymentRow = BillingExportOverview['payments'][number];
type PaymentsSortKey = 'paymentDate' | 'project' | 'client' | 'method' | 'amount';
type ReceivablesSortKey = 'project' | 'client' | 'approved' | 'paid' | 'balance' | 'lastPayment' | 'status';
type SortDirection = 'asc' | 'desc';

function paymentChipClass(status: BillingPaymentStatus) {
  switch (status) {
    case 'UNPAID':
      return 'bg-destructive/10 text-destructive';
    case 'PARTIALLY_PAID':
      return 'bg-warning/20 text-warning';
    case 'PAID':
      return 'bg-status-approved-bg text-status-approved';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function formatBalance(value: number) {
  if (value < 0) return `Overpaid ${formatCurrency(Math.abs(value))}`;
  return formatCurrency(value);
}

function triggerDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function csvEscape(value: string) {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function parsePaymentReferenceAndNote(note?: string | null) {
  const raw = note ?? '';
  const [first = '', ...rest] = raw.split('\n');
  if (first.startsWith('Ref: ')) {
    return {
      reference: first.replace('Ref: ', '').trim(),
      note: rest.join('\n').trim(),
    };
  }
  return { reference: '', note: raw.trim() };
}

function formatFollowupMenuDate(value?: string | null) {
  if (!value) return null;
  return new Date(value).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getLastFollowupSummary(project: BillingProjectItem) {
  const latest = project.paymentFollowupHistory?.[0]?.sentAt ?? project.lastPaymentFollowupAt;
  const formatted = formatFollowupMenuDate(latest);
  return formatted ? `Last follow-up: ${formatted}` : 'No follow-up yet';
}

export default function BillingExport() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentsHelpOpen, setPaymentsHelpOpen] = useState(false);
  const paymentMethodOptions = useMemo<Array<{ value: ProjectPaymentMethod; label: string }>>(
    () => [
      { value: 'CASH', label: t('billingExport.methods.cash') },
      { value: 'CHECK', label: t('billingExport.methods.check') },
      { value: 'CARD', label: t('billingExport.methods.card') },
      { value: 'ZELLE', label: t('billingExport.methods.zelle') },
      { value: 'ACH', label: t('billingExport.methods.ach') },
      { value: 'OTHER', label: t('billingExport.methods.other') },
    ],
    [t],
  );

  const paymentStatusLabel: Record<BillingPaymentStatus, string> = {
    UNPAID: t('billingExport.status.unpaid'),
    PARTIALLY_PAID: t('billingExport.status.partiallyPaid'),
    PAID: t('billingExport.status.paid'),
  };

  const formatMethod = useCallback(
    (method: ProjectPaymentMethod) =>
      paymentMethodOptions.find((option) => option.value === method)?.label ?? method,
    [paymentMethodOptions],
  );

  const formatBalanceLabel = (value: number) => {
    if (value < 0) return t('billingExport.overpaid', { amount: formatCurrency(Math.abs(value)) });
    return formatCurrency(value);
  };

  const [activeTab, setActiveTab] = useState<'payments' | 'receivables'>('receivables');
  const [paymentsSort, setPaymentsSort] = useState<{ key: PaymentsSortKey; direction: SortDirection }>({
    key: 'paymentDate',
    direction: 'desc',
  });
  const [receivablesSort, setReceivablesSort] = useState<{ key: ReceivablesSortKey; direction: SortDirection }>({
    key: 'balance',
    direction: 'desc',
  });

  const [dateRange, setDateRange] = useState<BillingDateRange>('last_30_days');
  const [statusFilter, setStatusFilter] = useState<BillingPaymentStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [ledgerProject, setLedgerProject] = useState<BillingProjectItem | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState<Awaited<ReturnType<typeof fetchProjectBillingLedger>> | null>(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<ProjectPaymentItem | null>(null);
  const [paymentTargetProject, setPaymentTargetProject] = useState<BillingProjectItem | null>(null);
  const [paymentModalStandalone, setPaymentModalStandalone] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState<ProjectPaymentMethod>('CASH');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [savingPayment, setSavingPayment] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState<string | null>(null);
  const [exportingProjectId, setExportingProjectId] = useState<string | null>(null);
  const [sendingFollowupProjectId, setSendingFollowupProjectId] = useState<string | null>(null);
  const [paymentFollowupTarget, setPaymentFollowupTarget] = useState<BillingProjectItem | null>(null);
  const [exportingCollectedPdf, setExportingCollectedPdf] = useState(false);
  const [exportingReceivablePdf, setExportingReceivablePdf] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'payments' || tab === 'receivables') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  useEffect(() => {
    if (dateRange !== 'custom') return;

    const today = new Date();
    const todayValue = today.toISOString().slice(0, 10);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayValue = yesterday.toISOString().slice(0, 10);

    if (!startDate) {
      setStartDate(yesterdayValue);
    }

    if (!endDate) {
      setEndDate(todayValue);
    }
  }, [dateRange, startDate, endDate]);

  const listOverviewQuery = useBillingOverview({
    dateRange,
    status: statusFilter,
    search,
    startDate: dateRange === 'custom' ? startDate || undefined : undefined,
    endDate: dateRange === 'custom' ? endDate || undefined : undefined,
    includeArchived,
  });

  const kpiOverviewQuery = useBillingOverview({
    dateRange,
    status: 'all',
    search,
    startDate: dateRange === 'custom' ? startDate || undefined : undefined,
    endDate: dateRange === 'custom' ? endDate || undefined : undefined,
    includeArchived,
  });

  const items = useMemo(() => listOverviewQuery.data?.items ?? EMPTY_ITEMS, [listOverviewQuery.data?.items]);
  const payments = useMemo(() => listOverviewQuery.data?.payments ?? [], [listOverviewQuery.data?.payments]);
  const debtorItems = useMemo(() => items.filter((item) => item.balance > 0), [items]);
  const sortedDebtorItems = useMemo(() => {
    const direction = receivablesSort.direction === 'asc' ? 1 : -1;
    return [...debtorItems].sort((left, right) => {
      let comparison = 0;
      if (receivablesSort.key === 'project') comparison = left.projectName.localeCompare(right.projectName);
      if (receivablesSort.key === 'client') comparison = (left.clientName ?? '').localeCompare(right.clientName ?? '');
      if (receivablesSort.key === 'approved') comparison = left.approvedAmount - right.approvedAmount;
      if (receivablesSort.key === 'paid') comparison = left.paidAmount - right.paidAmount;
      if (receivablesSort.key === 'balance') comparison = left.balance - right.balance;
      if (receivablesSort.key === 'lastPayment') {
        comparison =
          new Date(left.lastPayment?.date ?? 0).getTime() -
          new Date(right.lastPayment?.date ?? 0).getTime();
      }
      if (receivablesSort.key === 'status') comparison = left.paymentStatus.localeCompare(right.paymentStatus);
      return comparison * direction;
    });
  }, [debtorItems, receivablesSort.direction, receivablesSort.key]);
  const sortedPayments = useMemo(() => {
    const direction = paymentsSort.direction === 'asc' ? 1 : -1;
    return [...payments].sort((left, right) => {
      let comparison = 0;
      if (paymentsSort.key === 'paymentDate') {
        comparison = new Date(left.paymentDate).getTime() - new Date(right.paymentDate).getTime();
      }
      if (paymentsSort.key === 'project') comparison = left.projectName.localeCompare(right.projectName);
      if (paymentsSort.key === 'client') comparison = (left.clientName ?? '').localeCompare(right.clientName ?? '');
      if (paymentsSort.key === 'method') comparison = formatMethod(left.method).localeCompare(formatMethod(right.method));
      if (paymentsSort.key === 'amount') comparison = left.amount - right.amount;
      return comparison * direction;
    });
  }, [formatMethod, payments, paymentsSort.direction, paymentsSort.key]);
  const resolveProjectItemForPayment = useCallback((payment: PaymentRow): BillingProjectItem => {
    const existing = items.find((item) => item.projectId === payment.projectId);
    if (existing) return existing;

    return {
      projectId: payment.projectId,
      projectName: payment.projectName,
      projectCode: payment.projectCode ?? null,
      clientId: payment.clientId ?? null,
      clientName: payment.clientName ?? null,
      projectStatus: 'ACTIVE',
      archivedAt: null,
      approvedAmount: 0,
      paidAmount: 0,
      balance: 0,
      paymentStatus: 'PAID',
      collectedInRange: payment.amount,
      lastPayment: {
        date: payment.paymentDate,
        amount: payment.amount,
      },
    };
  }, [items]);
  const paymentProjectOptions = useMemo(() => {
    const byId = new Map<string, BillingProjectItem>();
    items.forEach((item) => byId.set(item.projectId, item));
    payments.forEach((payment) => {
      if (!byId.has(payment.projectId)) {
        byId.set(payment.projectId, resolveProjectItemForPayment(payment));
      }
    });
    return Array.from(byId.values()).sort((left, right) =>
      left.projectName.localeCompare(right.projectName),
    );
  }, [items, payments, resolveProjectItemForPayment]);
  const kpis = kpiOverviewQuery.data?.kpis ?? DEFAULT_KPIS;
  const isLoading = listOverviewQuery.isLoading && !listOverviewQuery.data;
  const error = listOverviewQuery.error instanceof Error ? listOverviewQuery.error.message : null;
  const isRefreshing =
    (listOverviewQuery.isFetching && Boolean(listOverviewQuery.data)) ||
    (kpiOverviewQuery.isFetching && Boolean(kpiOverviewQuery.data));
  const currentOutstandingBalance = Number(paymentTargetProject?.balance ?? 0);
  const enteredPaymentAmount = Number(paymentAmount || 0);
  const showOverpaymentWarning =
    paymentTargetProject &&
    currentOutstandingBalance > 0 &&
    Number.isFinite(enteredPaymentAmount) &&
    enteredPaymentAmount > currentOutstandingBalance;

  const togglePaymentsSort = (key: PaymentsSortKey) => {
    setPaymentsSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const toggleReceivablesSort = (key: ReceivablesSortKey) => {
    setReceivablesSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const renderSortIndicator = (
    current: { key: PaymentsSortKey | ReceivablesSortKey; direction: SortDirection },
    key: PaymentsSortKey | ReceivablesSortKey,
  ) => {
    const isActive = current.key === key;
    const SortIcon = isActive ? (current.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowDownUp;

    return (
      <SortIcon
        aria-hidden="true"
        className={`h-3.5 w-3.5 ${isActive ? 'text-primary' : 'text-muted-foreground/60'}`}
      />
    );
  };

  const openLedger = async (project: BillingProjectItem) => {
    setLedgerProject(project);
    setLedgerLoading(true);
    try {
      const result = await fetchProjectBillingLedger(project.projectId, {
        dateRange,
        startDate: dateRange === 'custom' ? startDate || undefined : undefined,
        endDate: dateRange === 'custom' ? endDate || undefined : undefined,
      });
      setLedgerData(result);
    } catch (e) {
      toast({
        title: t('billingExport.toasts.unableToLoadLedgerTitle'),
        description: e instanceof Error ? e.message : t('common.tryAgain'),
        variant: 'destructive',
      });
      setLedgerProject(null);
      setLedgerData(null);
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeLedger = () => {
    if (savingPayment || deletingPaymentId) return;
    setLedgerProject(null);
    setLedgerData(null);
    setLedgerLoading(false);
  };

  const openAddPayment = (project: BillingProjectItem) => {
    setPaymentModalStandalone(false);
    setPaymentTargetProject(project);
    setEditingPayment(null);
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod('CASH');
    setPaymentReference('');
    setPaymentNote('');
    setPaymentModalOpen(true);
  };

  const openEditPayment = (project: BillingProjectItem, payment: ProjectPaymentItem) => {
    setPaymentModalStandalone(false);
    setPaymentTargetProject(project);
    setEditingPayment(payment);
    setPaymentAmount(String(payment.amount));
    setPaymentDate(payment.paymentDate.slice(0, 10));
    setPaymentMethod(payment.method);

    const note = payment.note ?? '';
    const [first, ...rest] = note.split('\n');
    if (first.startsWith('Ref: ')) {
      setPaymentReference(first.replace('Ref: ', '').trim());
      setPaymentNote(rest.join('\n').trim());
    } else {
      setPaymentReference('');
      setPaymentNote(note);
    }

    setPaymentModalOpen(true);
  };

  const openStandaloneAddPayment = () => {
    setPaymentModalStandalone(true);
    setPaymentTargetProject(null);
    setEditingPayment(null);
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod('CASH');
    setPaymentReference('');
    setPaymentNote('');
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    if (savingPayment) return;
    setPaymentModalOpen(false);
    setEditingPayment(null);
    setPaymentTargetProject(null);
    setPaymentModalStandalone(false);
    if (searchParams.get('recordPayment') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('recordPayment');
      setSearchParams(next, { replace: true });
    }
  };

  const refreshBillingViews = async (targetProjectId?: string) => {
    await Promise.all([
      invalidateProjectData(queryClient),
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] }),
    ]);

    if (ledgerProject && targetProjectId && ledgerProject.projectId === targetProjectId) {
      await openLedger(ledgerProject);
    }
  };

  const savePayment = async () => {
    if (!paymentTargetProject) return;

    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast({ title: t('billingExport.toasts.invalidAmountTitle'), description: t('billingExport.toasts.invalidAmountDescription'), variant: 'destructive' });
      return;
    }

    if (!paymentDate) {
      toast({ title: t('billingExport.toasts.paymentDateRequired'), variant: 'destructive' });
      return;
    }

    const mergedNote = [
      paymentReference.trim() ? `Ref: ${paymentReference.trim()}` : '',
      paymentNote.trim(),
    ]
      .filter(Boolean)
      .join('\n');

    setSavingPayment(true);
    try {
      if (editingPayment) {
        await updateProjectPayment(paymentTargetProject.projectId, editingPayment.id, {
          amount,
          paymentDate,
          method: paymentMethod,
          note: mergedNote || undefined,
        });
        toast({ title: t('billingExport.toasts.paymentUpdated') });
      } else {
        await createProjectPayment(paymentTargetProject.projectId, {
          amount,
          paymentDate,
          method: paymentMethod,
          note: mergedNote || undefined,
        });
        toast({ title: t('billingExport.toasts.paymentRecorded') });
      }
      closePaymentModal();
      await refreshBillingViews(paymentTargetProject.projectId);
    } catch (e) {
      toast({
        title: editingPayment ? t('billingExport.toasts.updateFailedTitle') : t('common.createFailed'),
        description: e instanceof Error ? e.message : t('billingExport.toasts.unableToSavePayment'),
        variant: 'destructive',
      });
    } finally {
      setSavingPayment(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('recordPayment') !== '1') return;
    if (paymentModalOpen || savingPayment) return;
    openStandaloneAddPayment();
  }, [paymentModalOpen, savingPayment, searchParams]);

  const onDeletePayment = async (project: BillingProjectItem, payment: ProjectPaymentItem) => {
    const confirmed = window.confirm(t('billingExport.confirmDeletePayment', { amount: formatCurrency(payment.amount) }));
    if (!confirmed) return;

    setDeletingPaymentId(payment.id);
    try {
      await deleteProjectPayment(project.projectId, payment.id);
      toast({ title: t('billingExport.toasts.paymentDeleted') });
      await refreshBillingViews(project.projectId);
    } catch (e) {
      toast({
        title: t('billingExport.toasts.deleteFailedTitle'),
        description: e instanceof Error ? e.message : t('billingExport.toasts.unableToDeletePayment'),
        variant: 'destructive',
      });
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const exportStatement = async (project: BillingProjectItem) => {
    setExportingProjectId(project.projectId);
    try {
      const result = await exportProjectStatementPdf(project.projectId);
      triggerDownload(result.url, result.fileName);
      toast({ title: t('billingExport.toasts.statementPdfGenerated'), description: result.fileName });
    } catch (e) {
      toast({
        title: t('billingExport.toasts.exportFailedTitle'),
        description: e instanceof Error ? e.message : t('billingExport.toasts.unableToExportStatementPdf'),
        variant: 'destructive',
      });
    } finally {
      setExportingProjectId(null);
    }
  };

  const exportPaymentsCsv = async (project: BillingProjectItem) => {
    setExportingProjectId(project.projectId);
    try {
      const result = await exportProjectPaymentsCsv(project.projectId);
      triggerDownload(result.url, result.fileName);
      toast({ title: t('billingExport.toasts.paymentsCsvGenerated'), description: result.fileName });
    } catch (e) {
      toast({
        title: t('billingExport.toasts.exportFailedTitle'),
        description: e instanceof Error ? e.message : t('billingExport.toasts.unableToExportPaymentsCsv'),
        variant: 'destructive',
      });
    } finally {
      setExportingProjectId(null);
    }
  };

  const sendPaymentFollowup = async (
    project: BillingProjectItem,
    deliveryMethod: ApprovalDeliveryMethod,
    smsConsentConfirmed?: boolean,
  ) => {
    setSendingFollowupProjectId(project.projectId);
    try {
      const result = await sendProjectPaymentFollowup(project.projectId, {
        deliveryMethod,
        smsConsentConfirmed,
      });
      const sentLabel =
        result.sentChannels.length === 2
          ? 'Email and SMS sent'
          : result.sentChannels[0] === 'sms'
            ? 'SMS sent'
            : 'Email sent';
      const failureLabel = result.failedChannels.length
        ? ` Failed: ${result.failedChannels.map((item) => item.channel.toUpperCase()).join(', ')}.`
        : '';
      toast({
        title: t('billingExport.toasts.paymentFollowupSent'),
        description: `${sentLabel}.${failureLabel}`,
      });
      setPaymentFollowupTarget(null);
      await refreshBillingViews(project.projectId);
    } catch (e) {
      toast({
        title: t('billingExport.toasts.followUpFailedTitle'),
        description: e instanceof Error ? e.message : t('billingExport.toasts.unableToSendFollowupEmail'),
        variant: 'destructive',
      });
    } finally {
      setSendingFollowupProjectId(null);
    }
  };

  const exportCollectedPayments = async () => {
    setExportingCollectedPdf(true);
    try {
      const result = await exportCollectedPaymentsPdf({
        dateRange,
        startDate: dateRange === 'custom' ? startDate || undefined : undefined,
        endDate: dateRange === 'custom' ? endDate || undefined : undefined,
      });
      triggerDownload(result.url, result.fileName);
      toast({
        title: t('billingExport.toasts.collectedPaymentsPdfGenerated'),
        description: result.fileName,
      });
    } catch (e) {
      toast({
        title: t('billingExport.toasts.exportFailedTitle'),
        description: e instanceof Error ? e.message : t('billingExport.toasts.unableToGenerateCollectedPaymentsPdf'),
        variant: 'destructive',
      });
    } finally {
      setExportingCollectedPdf(false);
    }
  };

  const exportReceivablePayments = async () => {
    setExportingReceivablePdf(true);
    try {
      const result = await exportReceivablePaymentsPdf({
        dateRange,
        startDate: dateRange === 'custom' ? startDate || undefined : undefined,
        endDate: dateRange === 'custom' ? endDate || undefined : undefined,
      });
      triggerDownload(result.url, result.fileName);
      toast({
        title: t('billingExport.toasts.receivableReportPdfGenerated'),
        description: result.fileName,
      });
    } catch (e) {
      toast({
        title: t('billingExport.toasts.exportFailedTitle'),
        description: e instanceof Error ? e.message : t('billingExport.toasts.unableToGenerateReceivableReportPdf'),
        variant: 'destructive',
      });
    } finally {
      setExportingReceivablePdf(false);
    }
  };

  const exportCurrentCsv = () => {
    setExportingCsv(true);
    try {
      const lines =
        activeTab === 'receivables'
          ? [
              ['Project', 'Client', 'Approved', 'Paid', 'Balance', 'Last payment', 'Payment status']
                .map(csvEscape)
                .join(','),
              ...sortedDebtorItems.map((item) =>
                [
                  item.projectName,
                  item.clientName ?? '-',
                  formatCurrency(item.approvedAmount),
                  formatCurrency(item.paidAmount),
                  formatBalance(item.balance),
                  item.lastPayment
                    ? `${new Date(item.lastPayment.date).toLocaleDateString()} · ${formatCurrency(item.lastPayment.amount)}`
                    : '—',
                  paymentStatusLabel[item.paymentStatus],
                ]
                  .map((value) => csvEscape(String(value)))
                  .join(','),
              ),
            ]
          : [
              ['Payment date', 'Project', 'Client', 'Method', 'Amount', 'Reference', 'Note']
                .map(csvEscape)
                .join(','),
              ...sortedPayments.map((payment) => {
                const parsed = parsePaymentReferenceAndNote(payment.note);
                return [
                  new Date(payment.paymentDate).toLocaleDateString(),
                  payment.projectName,
                  payment.clientName ?? '-',
                  formatMethod(payment.method),
                  formatCurrency(payment.amount),
                  parsed.reference || '-',
                  parsed.note || '-',
                ]
                  .map((value) => csvEscape(String(value)))
                  .join(',');
              }),
            ];

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const objectUrl = URL.createObjectURL(blob);
      const fileName =
        activeTab === 'receivables'
          ? `workscope-debtor-projects-${new Date().toISOString().slice(0, 10)}.csv`
          : `workscope-payments-received-${new Date().toISOString().slice(0, 10)}.csv`;
      triggerDownload(objectUrl, fileName);
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } finally {
      setExportingCsv(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title={
          <div className="flex items-center gap-2">
            <span>{t('billingExport.title')}</span>
            <Popover open={paymentsHelpOpen} onOpenChange={setPaymentsHelpOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-transparent text-muted-foreground transition-colors hover:bg-muted/30 hover:text-[#0C1F4F]"
                  aria-label={t('billingExport.help.ariaLabel')}
                >
                  <Info className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[390px] border-border/80 bg-white p-4 shadow-lg"
              >
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0C1F4F]">{t('billingExport.help.title')}</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{t('billingExport.help.subtitle')}</p>
                  </div>
                  <ul className="space-y-2 text-sm leading-6 text-muted-foreground">
                    <li>{t('billingExport.help.items.kpis')}</li>
                    <li>{t('billingExport.help.items.debtorProjects')}</li>
                    <li>{t('billingExport.help.items.paymentsReceived')}</li>
                    <li>{t('billingExport.help.items.recording')}</li>
                  </ul>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
        actions={
          <div className="flex items-center gap-2">
            <RefreshingIndicator visible={isRefreshing} />
            <Button
              className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
              onClick={openStandaloneAddPayment}
            >
              <Plus className="h-4 w-4" />
              {t('billingExport.recordPayment')}
            </Button>
          </div>
        }
      />

      <SendPaymentFollowUpDialog
        open={Boolean(paymentFollowupTarget)}
        onOpenChange={(open) => {
          if (!open) setPaymentFollowupTarget(null);
        }}
        target={
          paymentFollowupTarget
            ? {
                projectName: paymentFollowupTarget.projectName,
                clientName: paymentFollowupTarget.clientName ?? null,
                clientEmail: paymentFollowupTarget.clientEmail ?? null,
                clientPhone: paymentFollowupTarget.clientPhone ?? null,
              }
            : null
        }
        loading={
          Boolean(paymentFollowupTarget) &&
          sendingFollowupProjectId === paymentFollowupTarget?.projectId
        }
        smsAllowedByPlan={user?.currentPlan !== 'STARTER'}
        history={paymentFollowupTarget?.paymentFollowupHistory ?? []}
        onConfirm={async (deliveryMethod, smsConsentConfirmed) => {
          if (!paymentFollowupTarget) return;
          await sendPaymentFollowup(
            paymentFollowupTarget,
            deliveryMethod,
            smsConsentConfirmed,
          );
        }}
      />

      <div className="grid grid-cols-1 gap-3 min-[1140px]:grid-cols-3">
        <KpiCard
          title={t('billingExport.kpis.receivable')}
          value={formatCurrency(kpis.receivable)}
          icon={DollarSign}
          tone="warning"
          action={
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                void exportReceivablePayments();
              }}
              disabled={
                exportingReceivablePdf ||
                exportingCollectedPdf ||
                (dateRange === 'custom' && (!startDate || !endDate))
              }
              className="inline-flex items-center gap-1 text-xs font-medium text-warning hover:underline disabled:pointer-events-none disabled:opacity-50"
            >
              {exportingReceivablePdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
              {t('billingExport.downloadReport')}
            </button>
          }
        />
        <KpiCard
          title={t('billingExport.kpis.collectedInRange')}
          value={formatCurrency(kpis.collected)}
          icon={FileDown}
          tone="success"
          action={
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                void exportCollectedPayments();
              }}
              disabled={
                exportingCollectedPdf ||
                exportingReceivablePdf ||
                (dateRange === 'custom' && (!startDate || !endDate))
              }
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline disabled:pointer-events-none disabled:opacity-50"
            >
              {exportingCollectedPdf ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
              {t('billingExport.downloadReport')}
            </button>
          }
        />
        <KpiCard title={t('billingExport.kpis.outstandingProjects')} value={kpis.outstandingProjects} icon={Folder} tone="info" />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <div className="hidden sm:flex sm:flex-wrap sm:items-center sm:justify-end sm:gap-2">
          {dateRange === 'custom' ? (
            <>
              <Input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="w-[170px]"
              />
              <Input
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="w-[170px]"
              />
            </>
          ) : null}
          <select
            className="h-9 rounded-md border bg-card px-3 text-sm text-foreground min-w-[170px]"
            value={dateRange}
            onChange={(event) => setDateRange(event.target.value as BillingDateRange)}
          >
            <option value="this_month">{t('billingExport.ranges.thisMonth')}</option>
            <option value="last_month">{t('billingExport.ranges.lastMonth')}</option>
            <option value="last_30_days">{t('billingExport.ranges.last30Days')}</option>
            <option value="last_90_days">{t('billingExport.ranges.last90Days')}</option>
            <option value="last_6_months">{t('billingExport.ranges.last6Months')}</option>
            <option value="custom">{t('billingExport.ranges.custom')}</option>
          </select>
        </div>

        <div className="flex flex-col gap-2 sm:hidden">
          <select
            className="h-9 rounded-md border bg-card px-3 text-sm text-foreground min-w-[170px]"
            value={dateRange}
            onChange={(event) => setDateRange(event.target.value as BillingDateRange)}
          >
            <option value="this_month">{t('billingExport.ranges.thisMonth')}</option>
            <option value="last_month">{t('billingExport.ranges.lastMonth')}</option>
            <option value="last_30_days">{t('billingExport.ranges.last30Days')}</option>
            <option value="last_90_days">{t('billingExport.ranges.last90Days')}</option>
            <option value="last_6_months">{t('billingExport.ranges.last6Months')}</option>
            <option value="custom">{t('billingExport.ranges.custom')}</option>
          </select>
          <div
            className={`grid w-full overflow-hidden transition-[grid-template-rows] duration-200 ease-out ${dateRange === 'custom' ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
          >
            <div className="min-h-0">
              <div className="flex flex-col gap-2 overflow-hidden pt-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="w-full"
                />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-col gap-3 min-[1140px]:flex-row min-[1140px]:items-center min-[1140px]:justify-between">
          <div className="w-full min-[1140px]:w-1/2">
            <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-accent p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab('receivables')}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                    activeTab === 'receivables'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  {t('billingExport.tabs.debtorProjects')}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('payments')}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition ${
                    activeTab === 'payments'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  {t('billingExport.tabs.paymentsReceived')}
                </button>
            </div>
          </div>
          <div className="flex w-full items-center justify-between gap-3 min-[1140px]:ml-6 min-[1140px]:w-auto min-[1140px]:justify-end">
            {activeTab === 'receivables' ? (
              <label className="inline-flex h-9 shrink-0 items-center gap-2 px-1 text-sm text-muted-foreground">
                <Checkbox checked={includeArchived} onCheckedChange={(checked) => setIncludeArchived(Boolean(checked))} />
                {t('billingExport.showArchived')}
              </label>
            ) : null}
            <button
              type="button"
              onClick={exportCurrentCsv}
              disabled={exportingCsv}
              className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-accent px-3 text-sm font-medium text-accent transition hover:bg-accent/10 disabled:pointer-events-none disabled:opacity-50"
            >
              {exportingCsv ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4" />
              )}
              {t('billingExport.downloadCsv')}
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <StateCard
          icon={AlertTriangle}
          tone="error"
          title={t('billingExport.unavailableTitle')}
          description={error}
          primaryActionLabel={t('dashboard.retry')}
          onPrimaryAction={() => {
            void listOverviewQuery.refetch();
            void kpiOverviewQuery.refetch();
          }}
        />
      ) : isLoading ? (
        <TableSkeleton rows={6} columns={8} />
      ) : activeTab === 'receivables' && debtorItems.length === 0 ? (
        <StateCard
          icon={FileX}
          title={t('billingExport.noDebtorProjectsTitle')}
          description={t('billingExport.noDebtorProjectsDescription')}
        />
      ) : activeTab === 'payments' && payments.length === 0 ? (
        <StateCard
          icon={FileX}
          title={t('billingExport.noPaymentsFoundTitle')}
          description={t('billingExport.noPaymentsFoundDescription')}
        />
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border bg-card overflow-hidden">
            {activeTab === 'receivables' ? (
              <>
                <div className="hidden min-[1140px]:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-4 py-3 text-primary bg-muted/35">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => toggleReceivablesSort('project')}>
                            {t('billingExport.table.project')}{renderSortIndicator(receivablesSort, 'project')}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-primary border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => toggleReceivablesSort('client')}>
                            {t('billingExport.table.client')}{renderSortIndicator(receivablesSort, 'client')}
                          </button>
                        </th>
                        <th className="text-right px-4 py-3 text-primary bg-muted/35 border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => toggleReceivablesSort('approved')}>
                            {t('billingExport.table.approved')}{renderSortIndicator(receivablesSort, 'approved')}
                          </button>
                        </th>
                        <th className="text-right px-4 py-3 text-primary border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => toggleReceivablesSort('paid')}>
                            {t('billingExport.table.paid')}{renderSortIndicator(receivablesSort, 'paid')}
                          </button>
                        </th>
                        <th className="text-right px-4 py-3 text-primary bg-muted/35 border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => toggleReceivablesSort('balance')}>
                            {t('billingExport.table.balance')}{renderSortIndicator(receivablesSort, 'balance')}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-primary border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => toggleReceivablesSort('lastPayment')}>
                            {t('billingExport.table.lastPayment')}{renderSortIndicator(receivablesSort, 'lastPayment')}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-primary bg-muted/35 border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => toggleReceivablesSort('status')}>
                            {t('billingExport.table.paymentStatus')}{renderSortIndicator(receivablesSort, 'status')}
                          </button>
                        </th>
                        <th className="w-[50px] px-0 py-3 text-primary border-l border-border/50" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDebtorItems.map((item) => (
                        <tr
                          key={item.projectId}
                          className="border-b last:border-0 hover:bg-muted/30"
                        >
                          <td className="px-4 py-3 bg-muted/35 cursor-pointer" onClick={() => void openLedger(item)}>
                            <p className="font-medium text-foreground">{item.projectName}</p>
                            <p className="text-xs text-muted-foreground">{item.projectCode ?? '—'}</p>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground border-l border-border/50 cursor-pointer" onClick={() => void openLedger(item)}>{item.clientName ?? '—'}</td>
                          <td className="px-4 py-3 text-right bg-muted/35 border-l border-border/50 cursor-pointer" onClick={() => void openLedger(item)}>{formatCurrency(item.approvedAmount)}</td>
                          <td className="px-4 py-3 text-right border-l border-border/50 cursor-pointer" onClick={() => void openLedger(item)}>{formatCurrency(item.paidAmount)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-accent bg-muted/35 border-l border-border/50 cursor-pointer" onClick={() => void openLedger(item)}>{formatBalanceLabel(item.balance)}</td>
                          <td className="px-4 py-3 text-muted-foreground border-l border-border/50 cursor-pointer" onClick={() => void openLedger(item)}>
                            {item.lastPayment
                              ? `${new Date(item.lastPayment.date).toLocaleDateString()} · ${formatCurrency(item.lastPayment.amount)}`
                              : '—'}
                          </td>
                          <td className="px-4 py-3 bg-muted/35 border-l border-border/50 cursor-pointer" onClick={() => void openLedger(item)}>
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentChipClass(item.paymentStatus)}`}>
                              {paymentStatusLabel[item.paymentStatus]}
                            </span>
                          </td>
                          <td
                            className="w-[50px] border-l border-border/50 p-0 text-right"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="flex h-full min-h-[72px] w-full items-center justify-center rounded-none hover:bg-secondary/60"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  {exportingProjectId === item.projectId ? (
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                  ) : (
                                    <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                                <DropdownMenuItem onClick={() => openAddPayment(item)}>
                                  {t('billingExport.actions.addPayment')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setPaymentFollowupTarget(item)}>
                                  {t('billingExport.actions.paymentFollowUp')}
                                </DropdownMenuItem>
                                <div className="px-2 pb-1 pt-0 text-[11px] leading-4 text-muted-foreground">
                                  {getLastFollowupSummary(item)}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => void exportStatement(item)}>
                                  {t('billingExport.actions.exportStatementPdf')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => void exportPaymentsCsv(item)}>
                                  {t('billingExport.actions.exportPaymentsCsv')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="hidden min-[767px]:block min-[1140px]:hidden space-y-3 p-3">
                  {sortedDebtorItems.map((item) => (
                    <div
                      key={`tablet-${item.projectId}`}
                      className="rounded-lg border bg-card p-3 space-y-2 cursor-pointer"
                      onClick={() => void openLedger(item)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-left">
                          <p className="font-semibold text-[#0C1F4F]">{item.projectName}</p>
                          <p className="text-xs text-muted-foreground">{item.clientName ?? t('billingExport.noClient')}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="flex h-full min-h-[56px] w-12 items-center justify-center rounded-md hover:bg-secondary/60"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                            <DropdownMenuItem onClick={() => openAddPayment(item)}>
                              Add payment
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPaymentFollowupTarget(item)}>
                              Payment follow-up
                            </DropdownMenuItem>
                            <div className="px-2 pb-1 pt-0 text-[11px] leading-4 text-muted-foreground">
                              {getLastFollowupSummary(item)}
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => void exportStatement(item)}>
                              Export statement PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => void exportPaymentsCsv(item)}>
                              Export payments CSV
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.table.approved')}</p>
                          <p className="text-[15px] font-medium">{formatCurrency(item.approvedAmount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.table.paid')}</p>
                          <p className="text-[15px] font-medium">{formatCurrency(item.paidAmount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.table.balance')}</p>
                          <p className="text-[15px] font-semibold text-accent">{formatBalanceLabel(item.balance)}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${paymentChipClass(item.paymentStatus)}`}>
                        {paymentStatusLabel[item.paymentStatus]}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="min-[767px]:hidden space-y-3 p-3">
                  {sortedDebtorItems.map((item) => (
                    <div
                      key={`mobile-${item.projectId}`}
                      className="rounded-lg border bg-card p-3 space-y-2 cursor-pointer"
                      onClick={() => void openLedger(item)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-left">
                          <p className="font-semibold text-[#0C1F4F]">{item.projectName}</p>
                          <p className="text-xs text-muted-foreground">{item.clientName ?? t('billingExport.noClient')}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="flex h-full min-h-[56px] w-12 items-center justify-center rounded-md hover:bg-secondary/60"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
                            <DropdownMenuItem onClick={() => openAddPayment(item)}>
                              Add payment
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPaymentFollowupTarget(item)}>
                              Payment follow-up
                            </DropdownMenuItem>
                            <div className="px-2 pb-1 pt-0 text-[11px] leading-4 text-muted-foreground">
                              {getLastFollowupSummary(item)}
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => void exportStatement(item)}>
                              Export statement PDF
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => void exportPaymentsCsv(item)}>
                              Export payments CSV
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.table.approved')}</p>
                          <p className="text-[15px] font-medium">{formatCurrency(item.approvedAmount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.table.paid')}</p>
                          <p className="text-[15px] font-medium">{formatCurrency(item.paidAmount)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.table.balance')}</p>
                          <p className="text-[15px] font-semibold text-accent">{formatBalanceLabel(item.balance)}</p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${paymentChipClass(item.paymentStatus)}`}>
                        {paymentStatusLabel[item.paymentStatus]}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="hidden min-[1140px]:block">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-4 py-3 text-primary bg-muted/35">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => togglePaymentsSort('paymentDate')}>
                            {t('billingExport.paymentDate')}{renderSortIndicator(paymentsSort, 'paymentDate')}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-primary border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => togglePaymentsSort('project')}>
                            {t('billingExport.table.project')}{renderSortIndicator(paymentsSort, 'project')}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-primary bg-muted/35 border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => togglePaymentsSort('client')}>
                            {t('billingExport.table.client')}{renderSortIndicator(paymentsSort, 'client')}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-primary border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => togglePaymentsSort('method')}>
                            {t('billingExport.method')}{renderSortIndicator(paymentsSort, 'method')}
                          </button>
                        </th>
                        <th className="text-right px-4 py-3 text-primary bg-muted/35 border-l border-border/50">
                          <button type="button" className="inline-flex cursor-pointer items-center gap-1 font-medium hover:text-primary/75" onClick={() => togglePaymentsSort('amount')}>
                            {t('billingExport.amount')}{renderSortIndicator(paymentsSort, 'amount')}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-primary border-l border-border/50">Reference / Note</th>
                        <th className="text-center px-4 py-3 text-primary border-l border-border/50">{t('billingExport.edit')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPayments.map((payment) => {
                        const parsedNote = parsePaymentReferenceAndNote(payment.note);
                        return (
                        <tr key={payment.id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="px-4 py-3 text-muted-foreground bg-muted/35">{new Date(payment.paymentDate).toLocaleDateString()}</td>
                          <td className="px-4 py-3 border-l border-border/50">
                            <p className="font-medium text-foreground">{payment.projectName}</p>
                            <p className="text-xs text-muted-foreground">{payment.projectCode ?? '—'}</p>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground bg-muted/35 border-l border-border/50">{payment.clientName ?? '—'}</td>
                          <td className="px-4 py-3 text-muted-foreground border-l border-border/50">{formatMethod(payment.method)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-accent bg-muted/35 border-l border-border/50">{formatCurrency(payment.amount)}</td>
                          <td className="max-w-[220px] px-4 py-3 text-xs text-muted-foreground border-l border-border/50">
                            {parsedNote.reference || parsedNote.note ? (
                              <div className="space-y-0.5">
                                {parsedNote.reference ? <p className="truncate" title={parsedNote.reference}>Ref: {parsedNote.reference}</p> : null}
                                {parsedNote.note ? <p className="truncate" title={parsedNote.note}>Note: {parsedNote.note}</p> : null}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-4 py-3 text-center border-l border-border/50">
                            <button
                              type="button"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-secondary hover:text-foreground"
                              onClick={() =>
                                openEditPayment(
                                  resolveProjectItemForPayment(payment),
                                  {
                                    id: payment.id,
                                    amount: payment.amount,
                                    paymentDate: payment.paymentDate,
                                    method: payment.method,
                                    note: payment.note ?? null,
                                    createdAt: payment.paymentDate,
                                    createdBy: {
                                      id: '',
                                      fullName: '',
                                      email: '',
                                    },
                                  },
                                )}
                              aria-label={t('billingExport.editPayment')}
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="hidden min-[767px]:block min-[1140px]:hidden space-y-3 p-3">
                  {sortedPayments.map((payment) => {
                    const parsedNote = parsePaymentReferenceAndNote(payment.note);
                    return (
                    <ExpandableListRow
                      key={`tablet-payment-${payment.id}`}
                      controlLayout="column"
                      icon={Wallet}
                      primary={<span>{payment.projectName}</span>}
                      secondary={<span className="truncate block">{payment.clientName ?? t('billingExport.noClient')}</span>}
                      status={<span className="text-[11px] font-semibold text-accent">{formatCurrency(payment.amount)}</span>}
                      details={
                        <ResponsiveDetailGrid
                          items={[
                            { label: 'Payment date', value: new Date(payment.paymentDate).toLocaleDateString() },
                            { label: 'Method', value: formatMethod(payment.method) },
                            { label: 'Project code', value: payment.projectCode ?? '—' },
                            { label: 'Reference', value: parsedNote.reference || '—' },
                            { label: 'Note', value: parsedNote.note || '—' },
                            {
                              label: 'Edit',
                              value: (
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 text-accent hover:underline"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEditPayment(
                                      resolveProjectItemForPayment(payment),
                                      {
                                        id: payment.id,
                                        amount: payment.amount,
                                        paymentDate: payment.paymentDate,
                                        method: payment.method,
                                        note: payment.note ?? null,
                                        createdAt: payment.paymentDate,
                                        createdBy: {
                                          id: '',
                                          fullName: '',
                                          email: '',
                                        },
                                      },
                                    );
                                  }}
                                >
                                  <Edit3 className="h-3.5 w-3.5" /> {t('billingExport.editPayment')}
                                </button>
                              ),
                            },
                          ]}
                        />
                      }
                    />
                    );
                  })}
                </div>

                <div className="min-[767px]:hidden space-y-3 p-3">
                  {sortedPayments.map((payment) => {
                    const parsedNote = parsePaymentReferenceAndNote(payment.note);
                    return (
                    <div key={`mobile-payment-${payment.id}`} className="rounded-lg border bg-card p-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-foreground">{payment.projectName}</p>
                          <p className="text-xs text-muted-foreground">{payment.clientName ?? t('billingExport.noClient')}</p>
                        </div>
                        <p className="font-semibold text-accent">{formatCurrency(payment.amount)}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.date')}</p>
                          <p className="font-medium">{new Date(payment.paymentDate).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">{t('billingExport.method')}</p>
                          <p className="font-medium">{formatMethod(payment.method)}</p>
                        </div>
                      </div>
                      {parsedNote.reference || parsedNote.note ? (
                        <div className="space-y-0.5 rounded-md bg-muted/35 px-2 py-1.5 text-[11px] text-muted-foreground">
                          {parsedNote.reference ? <p className="truncate">Ref: {parsedNote.reference}</p> : null}
                          {parsedNote.note ? <p className="truncate">Note: {parsedNote.note}</p> : null}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                        onClick={() =>
                          openEditPayment(
                            resolveProjectItemForPayment(payment),
                            {
                              id: payment.id,
                              amount: payment.amount,
                              paymentDate: payment.paymentDate,
                              method: payment.method,
                              note: payment.note ?? null,
                              createdAt: payment.paymentDate,
                              createdBy: {
                                id: '',
                                fullName: '',
                                email: '',
                              },
                            },
                          )}
                      >
                        <Edit3 className="h-3.5 w-3.5" /> {t('billingExport.editPayment')}
                      </button>
                    </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <SlideOverPanel open={Boolean(ledgerProject)} onClose={closeLedger} title={ledgerProject?.projectName ?? t('billingExport.projectLedger')} className="sm:w-[680px]">
        {ledgerLoading || !ledgerData ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t('billingExport.loadingLedger')}</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border bg-muted/20 p-3">
                <p className="text-xs text-muted-foreground">{t('billingExport.approvedTotal')}</p>
                <p className="text-lg font-semibold">{formatCurrency(ledgerData.summary.approvedAmount)}</p>
              </div>
              <div className="rounded-md border bg-muted/20 p-3">
                <p className="text-xs text-muted-foreground">{t('billingExport.paidTotal')}</p>
                <p className="text-lg font-semibold">{formatCurrency(ledgerData.summary.paidAmount)}</p>
              </div>
              <div className="rounded-md border bg-muted/20 p-3">
                <p className="text-xs text-muted-foreground">{t('billingExport.table.balance')}</p>
                <p className="text-lg font-semibold">{formatBalanceLabel(ledgerData.summary.balance)}</p>
              </div>
              <div className="rounded-md border bg-muted/20 p-3">
                <p className="text-xs text-muted-foreground">{t('billingExport.table.paymentStatus')}</p>
                <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${paymentChipClass(ledgerData.summary.paymentStatus)}`}>
                  {paymentStatusLabel[ledgerData.summary.paymentStatus]}
                </span>
              </div>
            </div>

            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-xs text-muted-foreground">{t('billingExport.lastPaymentFollowup')}</p>
              <p className="text-sm font-medium">
                {ledgerData.project.lastPaymentFollowupAt
                  ? new Date(ledgerData.project.lastPaymentFollowupAt).toLocaleString()
                  : 'Not sent yet'}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => openAddPayment(ledgerProject!)}>
                <Plus className="h-3.5 w-3.5 mr-1" /> {t('billingExport.actions.addPayment')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setPaymentFollowupTarget({
                    ...ledgerProject!,
                    lastPaymentFollowupAt:
                      ledgerData.project.lastPaymentFollowupAt ?? ledgerProject!.lastPaymentFollowupAt,
                    paymentFollowupHistory:
                      ledgerData.project.paymentFollowupHistory ?? ledgerProject!.paymentFollowupHistory,
                  })
                }
                disabled={sendingFollowupProjectId === ledgerProject?.projectId}
              >
                {sendingFollowupProjectId === ledgerProject?.projectId ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <FileText className="h-3.5 w-3.5 mr-1" />
                )}
                Payment follow-up
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void exportStatement(ledgerProject!)}
                disabled={exportingProjectId === ledgerProject?.projectId}
              >
                {exportingProjectId === ledgerProject?.projectId ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <FileDown className="h-3.5 w-3.5 mr-1" />
                )}
                Statement PDF
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void exportPaymentsCsv(ledgerProject!)}
                disabled={exportingProjectId === ledgerProject?.projectId}
              >
                {exportingProjectId === ledgerProject?.projectId ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5 mr-1" />
                )}
                Payments CSV
              </Button>
            </div>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{t('billingExport.approvedTickets')}</h3>
              {ledgerData.approvedTickets.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('billingExport.noApprovedTicketsForProject')}</p>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="px-3 py-2 text-left">{t('billingExport.ticketNumber')}</th>
                        <th className="px-3 py-2 text-left">{t('billingExport.approvedDate')}</th>
                        <th className="px-3 py-2 text-right">{t('billingExport.value')}</th>
                        <th className="px-3 py-2 text-center">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerData.approvedTickets.map((ticket) => (
                        <tr key={ticket.id} className="border-b last:border-0">
                          <td className="px-3 py-2">{ticket.ticketNumber}</td>
                          <td className="px-3 py-2 text-muted-foreground">{new Date(ticket.approvedDate).toLocaleDateString()}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(ticket.value)}</td>
                          <td className="px-3 py-2 text-center">
                            {ticket.pdfUrl ? (
                              <a href={ticket.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs hover:underline">
                                <FileText className="h-3.5 w-3.5" /> {t('billingExport.view')}
                              </a>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold">{t('billingExport.tabs.paymentsReceived')}</h3>
              {ledgerData.payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('billingExport.noPaymentsRecordedYet')}</p>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="px-3 py-2 text-left">{t('billingExport.date')}</th>
                        <th className="px-3 py-2 text-right">{t('billingExport.amount')}</th>
                        <th className="px-3 py-2 text-left">{t('billingExport.method')}</th>
                        <th className="px-3 py-2 text-left">Reference / Note</th>
                        <th className="px-3 py-2 text-left">{t('billingExport.addedBy')}</th>
                        <th className="px-3 py-2 text-right">{t('billingExport.actionsLabel')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerData.payments.map((payment) => {
                        const parsedNote = parsePaymentReferenceAndNote(payment.note);
                        return (
                        <tr key={payment.id} className="border-b last:border-0">
                          <td className="px-3 py-2 text-muted-foreground">{new Date(payment.paymentDate).toLocaleDateString()}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(payment.amount)}</td>
                          <td className="px-3 py-2">{formatMethod(payment.method)}</td>
                          <td className="max-w-[180px] px-3 py-2 text-xs text-muted-foreground">
                            {parsedNote.reference || parsedNote.note ? (
                              <div className="space-y-0.5">
                                {parsedNote.reference ? <p className="truncate" title={parsedNote.reference}>Ref: {parsedNote.reference}</p> : null}
                                {parsedNote.note ? <p className="truncate" title={parsedNote.note}>Note: {parsedNote.note}</p> : null}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{payment.createdBy.fullName || payment.createdBy.email}</td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => openEditPayment(ledgerProject!, payment)}
                                disabled={Boolean(deletingPaymentId)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => void onDeletePayment(ledgerProject!, payment)}
                                disabled={deletingPaymentId === payment.id}
                              >
                                {deletingPaymentId === payment.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('billingExport.delete')}
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </SlideOverPanel>

      <Dialog open={paymentModalOpen} onOpenChange={(open) => !open && closePaymentModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPayment ? 'Edit payment' : t('billingExport.recordPayment')}</DialogTitle>
            <DialogDescription>
              {paymentTargetProject?.projectName ?? t('billingExport.selectProjectForPayment')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {!editingPayment && paymentModalStandalone ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('billingExport.project')} *</label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                  value={paymentTargetProject?.projectId ?? ''}
                  onChange={(event) => {
                    const nextProject = paymentProjectOptions.find(
                      (item) => item.projectId === event.target.value,
                    ) ?? null;
                    setPaymentTargetProject(nextProject);
                  }}
                >
                  <option value="">{t('billingExport.selectProject')}</option>
                  {paymentProjectOptions.map((project) => (
                    <option key={project.projectId} value={project.projectId}>
                      {project.projectName}
                      {project.clientName ? ` · ${project.clientName}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
            {paymentTargetProject ? (
              <div className="flex flex-col gap-2 rounded-lg border border-border/70 bg-muted/25 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Outstanding balance
                  </p>
                  <p className="text-sm font-semibold text-[#0C1F4F]">
                    {formatBalanceLabel(currentOutstandingBalance)}
                  </p>
                </div>
                {currentOutstandingBalance > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setPaymentAmount(currentOutstandingBalance.toFixed(2))}
                  >
                    Pay full balance
                  </Button>
                ) : null}
              </div>
            ) : null}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('billingExport.amount')} *</label>
              <Input type="number" min="0" step="0.01" inputMode="decimal" value={paymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} />
              {showOverpaymentWarning ? (
                <p className="text-xs text-warning">
                  This payment is greater than the current balance. The project may become overpaid.
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('billingExport.paymentDate')}</label>
                <Input type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('billingExport.method')}</label>
                <select className="h-9 w-full rounded-md border bg-background px-3 text-sm" value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value as ProjectPaymentMethod)}>
                  {paymentMethodOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('billingExport.referenceOptional')}</label>
              <Input value={paymentReference} onChange={(event) => setPaymentReference(event.target.value)} placeholder={t('billingExport.referencePlaceholder')} />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('billingExport.noteOptional')}</label>
              <Textarea rows={3} value={paymentNote} onChange={(event) => setPaymentNote(event.target.value)} placeholder={t('billingExport.additionalDetails')} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closePaymentModal} disabled={savingPayment}>{t('createAccount.cancel')}</Button>
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => void savePayment()} disabled={savingPayment || !paymentTargetProject}>
              {savingPayment ? <Loader2 className="h-4 w-4 animate-spin" /> : t('billingExport.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
